import { createMaterialTopTabNavigator } from "@react-navigation/material-top-tabs";
import { useEffect, useState } from "react";
import { StyleSheet } from "react-native";
import { View } from "../../../components/shared/Themed";
import Colors from "../../../utils/constants/colors";
import useColorScheme from "../../../hooks/useColorScheme";
import ContainersDrawerNavigator from "./../Containers/ContainersDrawerNavigator";
import DeliveryDrawerNavigator from "./../Delivery/DeliveryDrawerNavigator";
import ReportsDrawerNavigator from "../Drawer/ReportsDrawerNavigator";
import { HomeTabParamList, RootTabScreenProps } from "../../../types";
import ScansDrawerNavigator from "../OperationScans/ScansDrawerNavigator";
import Spinner from "../../../components/shared/Spinner";
import { useSelector } from "react-redux";
import TabBarIcon from "../../../components/core/TabBarIcon";
import teamsConfig from "../../../utils/constants/teams";
import rolesConfig from "../../../utils/constants/roles";
import { userClaimInformation } from "../../../models/usersModel";
import OperationsDrawerNavigator from "../Operation/OperationsDrawerNavigator";
import ProductTrackerDrawerNavigator from "../../StandardUserNavigator/ProductTracker/ProductTrackerDrawerNavigator";
import { useTranslation } from "../../../utils/useTranslation";
import PortalOrdersDrawerNavigator from "../../StandardUserNavigator/PortalOrders/PortalOrdersDrawerNavigator";

const Tab = createMaterialTopTabNavigator<HomeTabParamList>();

export default function HomeTabNavigator() {
  const colorScheme = useColorScheme();
  const [userProfile, setUserProfile] = useState<userClaimInformation>();
  const [assignedRoles, setAssignedRoles] = useState<string[]>();
  const { t } = useTranslation();

  const { userInfo, userRoles, currentRole } = useSelector(
    (state: any) => state.configurations,
  );
  const { operation, purchasing, china, sales } = teamsConfig.teams;
  const { owner, admin } = rolesConfig.roles;

  useEffect(() => {
    setUserProfile(userInfo);
    setAssignedRoles(userRoles);
  }, [userInfo]);

  // Show loading spinner while user data is being loaded
  if (!userProfile || !currentRole || !assignedRoles) {
    return (
      <View
        style={{
          flexDirection: "column",
          width: "100%",
          height: "100%",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: colorScheme === "light" ? "#fafafa" : "#fafafa",
        }}
      >
        <Spinner
          size="small"
          color="#74BBFF"
          styles={{
            flexDirection: "row",
            justifySelf: "center",
            alignSelf: "center",
          }}
        />
      </View>
    );
  }

  return (
    <View
      style={{
        height: "100%",
        backgroundColor: colorScheme === "light" ? "#fafafa" : "#fafafa",
      }}
    >
      <Tab.Navigator
        initialRouteName="Reports"
        screenOptions={{
          tabBarActiveTintColor: Colors[colorScheme].tint,
          tabBarStyle: styles._tabBarStyle,
          tabBarLabelStyle: styles._tabBarLabelStyle,
          tabBarItemStyle: styles._tabBarItemStyle,
          swipeEnabled: false,
          lazy: true,
          lazyPlaceholder: () => {
            return (
              <View
                style={{
                  flexDirection: "column",
                  width: "100%",
                  height: "100%",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Spinner
                  size="small"
                  color="#74BBFF"
                  styles={{
                    flexDirection: "row",
                    justifySelf: "center",
                    alignSelf: "center",
                  }}
                />
              </View>
            );
          },
        }}
      >
        <Tab.Screen
          name="Reports"
          component={ReportsDrawerNavigator}
          options={({}: RootTabScreenProps<"Reports">) => ({
            title: t("reports"),
            tabBarIcon: ({ color }) => (
              <TabBarIcon name="archive" color={color} size={20} />
            ),
          })}
        />
        <Tab.Screen
          name="Containers"
          component={ContainersDrawerNavigator}
          options={({}: RootTabScreenProps<"Containers">) => ({
            title: t("containers"),
            tabBarIcon: ({ color }) => (
              <TabBarIcon name="ship" color={color} size={20} />
            ),
          })}
        />

        {([purchasing, china, operation, sales].includes(
          userProfile?.team?.toUpperCase(),
        ) ||
          [owner, admin].includes(currentRole.toUpperCase())) && (
          <Tab.Screen
            name="Tracker"
            component={ProductTrackerDrawerNavigator}
            options={({}: RootTabScreenProps<"ProductTracker">) => ({
              title: t("tracker"),
              tabBarIcon: ({ color }) => (
                <TabBarIcon name="calendar-o" color={color} size={20} />
              ),
            })}
          />
        )}

        {[sales].includes(userProfile?.team?.toUpperCase()) ||
          ([owner, admin].includes(currentRole.toUpperCase()) &&
            process.env.NODE_ENV !== "production" && (
              <Tab.Screen
                name="Customer"
                component={PortalOrdersDrawerNavigator}
                options={({}: RootTabScreenProps<"Customer">) => ({
                  title: t("customer"),
                  tabBarIcon: ({ color }) => (
                    <TabBarIcon name="male" color={color} size={20} />
                  ),
                })}
              />
            ))}

        {process.env.NODE_ENV !== "production" &&
          ([operation].includes(userProfile?.team?.toUpperCase()) ||
            [owner, admin].includes(currentRole.toUpperCase())) && (
            <Tab.Screen
              name="Operations"
              component={OperationsDrawerNavigator}
              options={({}: RootTabScreenProps<"Operations">) => ({
                title: t("operations"),
                tabBarIcon: ({ color }) => (
                  <TabBarIcon name="tasks" color={color} size={20} />
                ),
              })}
            />
          )}

        <Tab.Screen
          name="Delivery"
          component={DeliveryDrawerNavigator}
          options={({}: RootTabScreenProps<"Delivery">) => ({
            title: t("delivery"),
            tabBarIcon: ({ color }) => (
              <TabBarIcon name="truck" color={color} size={20} />
            ),
          })}
        />

        {userProfile?.team.toUpperCase() === operation &&
          assignedRoles?.includes(owner) && (
            <Tab.Screen
              name="OperationalScans"
              component={ScansDrawerNavigator}
              options={({}: RootTabScreenProps<"OperationalScans">) => ({
                title: t("production"),
                tabBarIcon: ({ color }) => (
                  <TabBarIcon name="steam" color={color} size={20} />
                ),
              })}
            />
          )}
      </Tab.Navigator>
    </View>
  );
}
const styles = StyleSheet.create({
  _tabBarStyle: {
    height: 60,
    width: "100%",
    alignSelf: "center",
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#fafafa",
    borderColor: "darkgrey",
    shadowColor: "#171717",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.2,
    shadowRadius: 4,
    elevation: 5,
  },
  _tabBarLabelStyle: {
    fontSize: 10,
    fontWeight: "bold",
    textTransform: "none",
    textAlign: "center",
  },
  _tabBarItemStyle: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});
